import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import SimpleBar from 'simplebar-react';

const BarChartData = ({
  title,
  data = [],
  xAxisDataKey = 'name',
  barDataKey = "value",
  xAxisLabel = '',
  yAxisLabel = '',
  barColor = "#184A7D",
  chartTitle = '',
  height = 250,
  tooltipLabelFormatter = label => label,
  barLabelFormatter = value => value,
  showLegend = false,
  bars = [
    {
      dataKey: 'value',
      name: 'Cantidad',
      fill: '#184A7D',
      label: false
    }
  ],
  barsize,
  barGap = 8,
  barCategoryGap = '20%',
  loading = false,
  extra = null,
}) => {
  const renderCustomizedAxisTick = ({ x, y, payload }) => {
    const idx = data.findIndex(item => item[xAxisDataKey] === payload.value);
    const fillColor = idx < 2 ? 'red' : '#007696';
    return (
      <text
        x={x}
        y={y + 15}
        textAnchor="middle"
        fill={fillColor}
        fontSize={11}
        fontFamily="ITC-medium, sans-serif"
      >
        {payload.value}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="w-full h-[250px] flex items-center justify-center bg-white rounded-[10px] shadow-md">
        <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-transparent rounded-full" />
        <p className="ml-3 text-gray-500">Cargando...</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-[250px] flex items-center justify-center bg-white rounded-[10px] shadow-md">
        <p className="text-gray-500">No hay datos disponibles</p>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {chartTitle &&
        <h3 className="mb-2 text-lg font-semibold">
          {chartTitle}
        </h3>
      }
      <div className="w-full h-full p-4 px-6 bg-white shadow-md rounded-[10px]">
        <div className="flex items-center justify-between pb-4">
          <h1 className="text-base text-blue font-itcbold">{title}</h1>
          {extra && <div className="flex-shrink-0">{extra}</div>}
        </div>

        {/* Versión móvil con scroll horizontal */}
        <div className="w-full overflow-x-auto lg:hidden">
          <div style={{
            width: `${Math.max(data.length * 80, 300)}px`,
            height: `${height}px`
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
                layout="horizontal"
                barSize={barsize}
                barGap={barGap}
                barCategoryGap={barCategoryGap}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E7E7" />
                <XAxis
                  dataKey={xAxisDataKey}
                  label={{ value: xAxisLabel, position: 'bottom', offset: 30 }}
                  /* tick={{
                    fontSize: 11,
                    fill: '#007696',
                    fontFamily: "ITC-medium, sans-serif",
                  }} */
                  tick={renderCustomizedAxisTick}
                  interval={0}
                  height={35}
                  tickLine={false}
                  tickMargin={3}
                  axisLine={{ stroke: '#E2E7E7' }}
                />
                <Tooltip labelFormatter={tooltipLabelFormatter} />

                {bars.map(({ dataKey, name, fill, label }, i) => (
                  <Bar
                    key={i}
                    dataKey={dataKey}
                    name={name}
                    fill={fill}
                    radius={[4, 4, 0, 0]}
                    label={
                      label ? {
                        position: 'top',
                        formatter: barLabelFormatter,
                        style: { fontSize: 12 }
                      } : false
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {showLegend && (
          <div className="pb-2 lg:hidden">
            <div className="flex flex-wrap justify-center gap-4">
              {bars.map(({ name, fill }, i) => (
                <div key={i} className="flex items-center">
                  <div
                    className="w-3 h-3 mr-2 rounded-full"
                    style={{ backgroundColor: fill }}
                  />
                  <span
                    className="text-sm font-itcbook"
                    style={{ color: fill }}
                  >
                    {name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Versión desktop */}
        <div className="hidden lg:block" style={{ height: `${height}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
              layout="horizontal"
              barSize={barsize}
              barGap={barGap}
              barCategoryGap={barCategoryGap}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E7E7" />
              <XAxis
                dataKey={xAxisDataKey}
                label={{ value: xAxisLabel, position: 'bottom', offset: 30 }}
                /* tick={{
                  fontSize: 11,
                  fill: '#007696',
                  fontFamily: "ITC-medium, sans-serif",
                }} */
                tick={renderCustomizedAxisTick}
                interval={0}
                height={35}
                tickLine={false}
                tickMargin={3}
                axisLine={{ stroke: '#E2E7E7' }}
              />
              <Tooltip labelFormatter={tooltipLabelFormatter} />
              {showLegend &&
                <Legend
                  layout="horizontal"
                  align="left"
                  verticalAlign="bottom"
                  iconType="circle"
                  formatter={(value, entry) => (
                    <span style={{
                      color: entry.color,
                      fontFamily: "ITC-book, sans-serif",
                      fontSize: 13.8,
                    }}>
                      {value}
                    </span>
                  )}
                />
              }
              {bars.map(({ dataKey, name, fill, label }, i) => (
                <Bar
                  key={i}
                  dataKey={dataKey}
                  name={name}
                  fill={fill}
                  radius={[4, 4, 0, 0]}
                  label={
                    label ? {
                      position: 'top',
                      formatter: barLabelFormatter,
                      style: { fontSize: 12 }
                    } : false
                  }
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default BarChartData;